import React, { useState, useRef } from "react";
import LandingHeader from "../components/layout/LandingHeader";
import LandingFooter from "../components/layout/LandingFooter";
import WhatsAppButton from "../components/common/WhatsAppButton";
import {
  Button,
  Input,
  Textarea,
  Select,
  Toast,
  Accordion,
  AccordionTab,
} from "../components/ui";
import {
  Headphones,
  Briefcase,
  Users,
  MessageCircle,
  Send,
  Lock,
} from "lucide-react";

function Contato() {
  const toast = useRef(null);

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const subjects = [
    { label: "Dúvida Geral", value: "duvida" },
    { label: "Suporte Técnico", value: "suporte" },
    { label: "Parceria Comercial", value: "comercial" },
    { label: "Novas Implementações", value: "novas" },
    { label: "Outros Assuntos", value: "outros" },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !email || !whatsapp || !subject || !message) {
      toast.current.show({
        severity: "warn",
        summary: "Campos Obrigatórios",
        detail: "Por favor, preencha todos os campos marcados com *.",
        life: 3000,
      });
      return;
    }

    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      toast.current.show({
        severity: "success",
        summary: "Mensagem Enviada!",
        detail:
          "Obrigado pelo contato. Responderemos seu e-mail em até 24 horas.",
        life: 4000,
      });
      // Clear fields
      setName("");
      setEmail("");
      setWhatsapp("");
      setSubject("");
      setMessage("");
    }, 1200);
  };

  return (
    <div className="min-h-screen flex flex-col bg-urb-dark text-slate-100 selection:bg-urb-primary selection:text-black">
      <Toast ref={toast} position="top-right" />
      <LandingHeader />

      <main className="flex-1 py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
          {/* Hero Header */}
          <div className="flex flex-col gap-2 mb-4">
            <span className="font-semibold text-xs sm:text-sm tracking-wider uppercase text-urb-primary">
              FALE CONOSCO
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black m-0 leading-tight">
              ESTAMOS AQUI{" "}
              <span className="text-urb-primary drop-shadow-[0_0_12px_rgba(138,230,0,0.3)]">
                PARA AJUDAR.
              </span>
            </h1>
            <p className="text-slate-400 text-base sm:text-lg m-0 max-w-2xl leading-relaxed">
              Tem dúvidas, sugestões ou quer levar a URB RACE para o seu evento?
              Fale com o nosso time.
            </p>
          </div>

          {/* Contact Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Side: Contact Channels */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              <h3 className="text-white font-extrabold text-lg m-0 tracking-wide uppercase">
                CANAIS DE ATENDIMENTO
              </h3>

              <div className="flex flex-col gap-3">
                {/* Suporte */}
                <div className="p-4 rounded-2xl border border-urb-border bg-urb-card/80 flex justify-between items-center hover:border-slate-700 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center shrink-0">
                      <Headphones className="w-5 h-5 text-urb-primary" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-white text-sm">
                        SUPORTE
                      </span>
                      <span className="text-xs text-slate-400 mb-0.5">
                        Dúvidas e ajuda com a plataforma
                      </span>
                      <span className="text-xs text-urb-primary font-semibold">
                        contato@urbrace.com.br
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-[10px] text-slate-400 hidden sm:block">
                    <span>Seg a Sex</span>
                    <br />
                    <span className="font-bold text-white">08h às 18h</span>
                  </div>
                </div>

                {/* Comercial */}
                <div className="p-4 rounded-2xl border border-urb-border bg-urb-card/80 flex justify-between items-center hover:border-slate-700 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center shrink-0">
                      <Briefcase className="w-5 h-5 text-urb-primary" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-white text-sm">
                        COMERCIAL
                      </span>
                      <span className="text-xs text-slate-400 mb-0.5">
                        Parcerias, orçamentos e propostas
                      </span>
                      <span className="text-xs text-urb-primary font-semibold">
                        contato@urbrace.com.br
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-[10px] text-slate-400 hidden sm:block">
                    <span>Seg a Sex</span>
                    <br />
                    <span className="font-bold text-white">08h às 18h</span>
                  </div>
                </div>

                {/* Organizadores */}
                <div className="p-4 rounded-2xl border border-urb-border bg-urb-card/80 flex justify-between items-center hover:border-slate-700 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-urb-primary" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-white text-sm">
                        ORGANIZADORES
                      </span>
                      <span className="text-xs text-slate-400 mb-0.5">
                        Onboarding e novas implementações
                      </span>
                      <span className="text-xs text-urb-primary font-semibold">
                        contato@urbrace.com.br
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-[10px] text-slate-400 hidden sm:block">
                    <span>Seg a Sex</span>
                    <br />
                    <span className="font-bold text-white">08h às 18h</span>
                  </div>
                </div>

                {/* WhatsApp */}
                <a
                  href="https://wa.me/5596981004733"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 rounded-2xl border border-emerald-800/40 bg-emerald-950/20 flex justify-between items-center hover:border-emerald-600 transition-all cursor-pointer no-underline text-inherit"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500 flex items-center justify-center shrink-0">
                      <MessageCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-white text-sm">
                        WHATSAPP
                      </span>
                      <span className="text-xs text-slate-400 mb-0.5">
                        Atendimento rápido e direto
                      </span>
                      <span className="text-xs text-emerald-400 font-semibold">
                        (96) 98100-4733
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-[10px] text-slate-400 hidden sm:block">
                    <span>Todos os dias</span>
                    <br />
                    <span className="font-bold text-white">08h às 22h</span>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Side: Message Form */}
            <div className="lg:col-span-7">
              <div className="bg-urb-card border border-urb-border rounded-3xl p-6 sm:p-8 flex flex-col gap-6 shadow-2xl">
                <h3 className="text-white font-extrabold text-lg m-0 tracking-wide uppercase">
                  ENVIE SUA MENSAGEM
                </h3>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-slate-300 font-medium">
                        Seu nome *
                      </label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Seu nome"
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-slate-300 font-medium">
                        E-mail *
                      </label>
                      <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="exemplo@email.com"
                        type="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-slate-300 font-medium">
                        WhatsApp *
                      </label>
                      <Input
                        value={whatsapp}
                        onChange={(e) => setWhatsapp(e.target.value)}
                        placeholder="(96) 99999-9999"
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-slate-300 font-medium">
                        Assunto *
                      </label>
                      <Select
                        value={subject}
                        options={subjects}
                        optionLabel="label"
                        onChange={(e) => setSubject(e.target.value?.value || e.target.value)}
                        placeholder="Selecione um assunto"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-slate-300 font-medium">
                      Mensagem *
                    </label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={5}
                      placeholder="Conte-nos como podemos ajudar..."
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full font-black text-sm uppercase py-3.5 tracking-wider mt-2 flex items-center justify-center gap-2 shadow-lg"
                    loading={loading}
                  >
                    <Send className="w-4 h-4" />
                    ENVIAR MENSAGEM
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-2">
                    <Lock className="w-3.5 h-3.5" />
                    <span>
                      Seus dados estão protegidos. Não compartilhamos suas
                      informações.
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Bottom Area: FAQ Accordion */}
          <div className="border-t border-urb-border pt-10 mt-6">
            <div className="max-w-3xl">
              <div className="flex justify-between items-baseline mb-6">
                <h3 className="text-white font-extrabold text-xl m-0 tracking-wide uppercase">
                  PERGUNTAS FREQUENTES
                </h3>
              </div>

              <Accordion>
                <AccordionTab header="Como funciona a inscrição em um evento?">
                  Basta navegar até a página de eventos, selecionar a corrida
                  desejada, preencher seus dados cadastrais, escolher o lote/kit
                  e realizar o pagamento seguro (via PIX, Cartão ou Boleto).
                </AccordionTab>
                <AccordionTab header="Quais são as taxas da plataforma?">
                  Nossas taxas variam dependendo da modalidade do evento e do
                  volume de inscrições, sendo cobradas por transação efetuada com
                  total transparência e sem taxas de adesão.
                </AccordionTab>
                <AccordionTab header="Como receber os pagamentos das inscrições?">
                  O repasse dos valores líquidos arrecadados é programado e
                  depositado diretamente via PIX ou transferência na conta
                  cadastrada do organizador de forma ágil e segura.
                </AccordionTab>
                <AccordionTab header="É possível integrar a URB RACE ao meu sistema?">
                  Sim! Disponibilizamos APIs abertas completas e Webhooks em
                  tempo real para sincronização com seus CRM, sistemas de
                  controle financeiro ou de cronometragem.
                </AccordionTab>
              </Accordion>
            </div>
          </div>
        </div>
      </main>

      <LandingFooter />

      {/* Botão Flutuante do WhatsApp */}
      <WhatsAppButton />
    </div>
  );
}

export default Contato;
